import React, { useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { loadLanguage } from '../i18n/config';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

const LanguageSelector = memo(() => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLanguageChange = useCallback(async (languageCode) => {
    if (i18n?.language === languageCode || isLoading) return;
    
    setIsLoading(true);
    try {
      await loadLanguage(languageCode);
      localStorage.setItem('preferred_language', languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  }, [i18n?.language, isLoading]);

  const currentLanguage = LANGUAGES?.find(lang => lang?.code === i18n?.language) || LANGUAGES?.[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] disabled:opacity-50"
        aria-label="Select language"
      >
        <Globe className="w-5 h-5" />
        <span className="text-2xl" role="img" aria-label={currentLanguage?.name}>
          {currentLanguage?.flag}
        </span>
        <span className="font-medium">{currentLanguage?.name}</span>
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            {LANGUAGES?.map((language) => (
              <button
                key={language?.code}
                onClick={() => handleLanguageChange(language?.code)}
                disabled={isLoading}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg min-h-[44px] disabled:opacity-50 ${
                  i18n?.language === language?.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                <span className="text-2xl" role="img" aria-label={language?.name}>
                  {language?.flag}
                </span>
                <span className="font-medium">{language?.name}</span>
                {i18n?.language === language?.code && (
                  <span className="ml-auto text-blue-600">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;